/**
 * Native bridge — abstracts Capacitor plugin calls with browser fallbacks.
 *
 * On the web bundle, the relevant Capacitor packages are not bundled; the
 * fallback paths are used. On the native shell, capacitor.config.json declares
 * the plugins and the bundle accesses them via `(window as any).Capacitor`.
 *
 * Keep this module dependency-free of @capacitor/* so the web build doesn't
 * pull native code.
 */

type Caps = {
  Plugins?: {
    Haptics?: {
      impact(opts: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>;
      notification(opts: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
      vibrate?: (opts: { duration: number }) => Promise<void>;
    };
    StatusBar?: {
      setStyle(opts: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }): Promise<void>;
      setBackgroundColor(opts: { color: string }): Promise<void>;
    };
    LocalNotifications?: {
      schedule(opts: { notifications: NotifSchedule[] }): Promise<void>;
      requestPermissions(): Promise<{ display: NotifPermissionState }>;
      checkPermissions(): Promise<{ display: NotifPermissionState }>;
      cancel(opts: { notifications: Array<{ id: number }> }): Promise<void>;
    };
    Purchases?: RCPurchases;
  };
};

/** Capacitor PermissionState — 'prompt-with-rationale' is Android-only. */
type NotifPermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

interface NotifSchedule {
  id: number;
  title: string;
  body: string;
  schedule?: { at?: Date; repeats?: boolean };
}

interface Product {
  id: string;
  title: string;
  price: string; // formatted string from store, locale-aware
  currency: string;
  amountMicros: number;
}

interface PurchaseResult {
  productId: string;
  receipt: string;
  status: 'completed' | 'pending' | 'failed';
  /** Set when status='failed' — the store/RC error so the UI isn't a silent no-op. */
  error?: string;
  /** True when the user dismissed the purchase sheet (not a real failure). */
  cancelled?: boolean;
}

function caps(): NonNullable<Caps['Plugins']> {
  return ((window as Window & { Capacitor?: Caps }).Capacitor?.Plugins ?? {}) as NonNullable<
    Caps['Plugins']
  >;
}

function isNative(): boolean {
  return Boolean(
    (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.(),
  );
}

/** True when running inside the Capacitor native shell (iOS / Android). */
export function isNativePlatform(): boolean {
  return isNative();
}

/**
 * Read the current notification permission WITHOUT prompting.
 *
 * On native, the browser Notification API in WKWebView does NOT reflect
 * UNUserNotificationCenter status — only the plugin's checkPermissions() does.
 * Falls back to the browser Notification API on web/PWA.
 */
export async function checkNotifPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  const ln = caps().LocalNotifications;
  if (ln?.checkPermissions) {
    try {
      const res = await ln.checkPermissions();
      if (res.display === 'granted' || res.display === 'denied') return res.display;
      return 'prompt';
    } catch {
      /* fall through to web API */
    }
  }
  if (typeof Notification === 'undefined') return 'prompt';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return 'prompt';
}

/** Haptic feedback. Maps to CoreHaptics (iOS) / Vibrator (Android) / vibrate API (web). */
export async function haptic(
  kind: 'light' | 'medium' | 'success' | 'warning' | 'error' = 'light',
): Promise<void> {
  const h = caps().Haptics;
  if (h) {
    try {
      if (kind === 'success' || kind === 'warning' || kind === 'error') {
        await h.notification({ type: kind.toUpperCase() as 'SUCCESS' | 'WARNING' | 'ERROR' });
      } else {
        await h.impact({ style: kind === 'medium' ? 'MEDIUM' : 'LIGHT' });
      }
      return;
    } catch {
      /* fall through */
    }
  }
  // Web fallback — Vibration API (Android Chrome only; no-op on iOS Safari).
  if ('vibrate' in navigator) {
    try {
      (navigator as Navigator & { vibrate: (ms: number | number[]) => void }).vibrate(
        kind === 'success' ? [10, 30, 10] : 12,
      );
    } catch {
      /* ignore */
    }
  }
}

/** Schedule a daily streak reminder at given local time (HH:MM). */
export async function scheduleStreakReminder(
  at: { hour: number; minute: number },
  title: string,
  body: string,
): Promise<boolean> {
  const ln = caps().LocalNotifications;
  if (!ln) return false;
  try {
    const perm = await ln.requestPermissions();
    if (perm.display !== 'granted') return false;
    const next = new Date();
    next.setHours(at.hour, at.minute, 0, 0);
    if (next.getTime() < Date.now()) next.setDate(next.getDate() + 1);
    await ln.schedule({
      notifications: [{ id: 1001, title, body, schedule: { at: next, repeats: true } }],
    });
    return true;
  } catch {
    return false;
  }
}

/** Cancel scheduled reminders. */
export async function cancelStreakReminder(): Promise<void> {
  const ln = caps().LocalNotifications;
  await ln?.cancel({ notifications: [{ id: 1001 }] });
}

/** Set native status-bar style to match active theme. No-op on web. */
export async function syncStatusBar(theme: 'light' | 'dark'): Promise<void> {
  const sb = caps().StatusBar;
  if (!sb || !isNative()) return;
  await sb.setStyle({ style: theme === 'dark' ? 'DARK' : 'LIGHT' });
  await sb.setBackgroundColor({ color: theme === 'dark' ? '#121212' : '#FAFAFA' });
}

/**
 * IAP backend = RevenueCat (@revenuecat/purchases-capacitor).
 *
 * SKU mapping: short id used across the UI ↔ full product id in App Store
 * Connect / Google Play. ASC namespace = `app.fudeto.*` (per
 * docs/v2.0/MASTERPIECE-v2.2.md §1.3 and shared/feedback_apple_reserved_productid.md).
 *
 * The plugin is reached through the native bridge registry (see getRC below) —
 * the RC JS module is never imported, keeping the web bundle slim and avoiding
 * a second bundled @capacitor/core runtime in the native path.
 */
const SKU_TO_PRODUCT_ID: Record<SkuId, string> = {
  streak_shield: 'app.fudeto.streak_shield',
  pro_monthly: 'app.fudeto.pro_monthly',
};

export type SkuId = 'streak_shield' | 'pro_monthly';

let rcConfigured = false;

type RCStoreProduct = {
  identifier: string;
  title?: string;
  priceString?: string;
  currencyCode?: string;
  price?: number;
};

type RCPurchases = {
  configure(opts: { apiKey: string; appUserID?: string | null }): Promise<void>;
  getProducts(opts: { productIdentifiers: string[] }): Promise<{
    products: RCStoreProduct[];
  }>;
  /** RC SDK v9+: takes a full StoreProduct object (from getProducts), not a string id. */
  purchaseStoreProduct(opts: { product: RCStoreProduct }): Promise<{
    transaction?: { transactionIdentifier?: string };
    customerInfo?: unknown;
  }>;
  restorePurchases(): Promise<{ customerInfo?: unknown }>;
  getCustomerInfo(): Promise<{ customerInfo?: unknown }>;
};

/**
 * Resolve the RevenueCat Purchases plugin — SYNCHRONOUSLY, via the native
 * bridge registry (`window.Capacitor.Plugins.Purchases`), same pattern as
 * Haptics / StatusBar above.
 *
 * Why not `await import('@revenuecat/purchases-capacitor')` (the old code):
 * Capacitor plugin proxies create a bridge-call function for EVERY property
 * access — including `then`. `typeof proxy.then === 'function'` makes the
 * proxy a *thenable*, so resolving a Promise with it (returning it from an
 * async function) makes the JS engine call `proxy.then(...)` = a native
 * "Purchases.then" call that never answers → every `await getRC()` hung
 * forever. Physical result: Subscribe / Restore buttons dead with zero
 * console output — the exact 2.1(b) "subscription button unresponsive"
 * Apple rejection on Build 67 (reproduced + bisected in iPad simulator,
 * 2026-06-11). A sync return never goes through promise resolution, so the
 * thenable trap can't fire.
 */
function getRC(): RCPurchases | null {
  if (!isNative()) return null;
  return caps().Purchases ?? null;
}

/** Initialize RevenueCat. Call once at app boot on native platforms.
 *  apiKey comes from RC dashboard (iOS / Android keys are different). */
export async function configureIAP(opts: {
  apiKey: string;
  appUserID?: string | null;
}): Promise<boolean> {
  if (rcConfigured) return true;
  const rc = getRC();
  if (!rc) return false;
  try {
    await rc.configure({ apiKey: opts.apiKey, appUserID: opts.appUserID ?? null });
    rcConfigured = true;
    return true;
  } catch {
    return false;
  }
}

/** Fetch product list from the store. Returns [] on web or before configure. */
export async function getProducts(): Promise<Product[]> {
  const rc = getRC();
  if (!rc) return [];
  try {
    const ids = Object.values(SKU_TO_PRODUCT_ID);
    const res = await rc.getProducts({ productIdentifiers: ids });
    return res.products.map((p) => ({
      id: p.identifier,
      title: p.title ?? p.identifier,
      price: p.priceString ?? '',
      currency: p.currencyCode ?? '',
      amountMicros: Math.round((p.price ?? 0) * 1_000_000),
    }));
  } catch {
    return [];
  }
}

/** Buy a product. Returns the transaction id (used as receipt handle by the worker).
 *  v1 economy: 1 consumable (streak_shield) + Fudeto Pro subscription. No ads.
 *
 *  RC SDK v9+ API: purchaseProduct(string) was removed; the correct flow is:
 *    1. getProducts({ productIdentifiers: [id] }) → RCStoreProduct object
 *    2. purchaseStoreProduct({ product }) → triggers StoreKit sheet
 */
export async function buyProduct(productId: SkuId): Promise<PurchaseResult> {
  const rc = getRC();
  if (!rc) return { productId, receipt: '', status: 'failed', error: 'In-app purchase unavailable on this device.' };
  const storeId = SKU_TO_PRODUCT_ID[productId];
  try {
    // Step 1: fetch the StoreProduct object (required by purchaseStoreProduct).
    const { products } = await rc.getProducts({ productIdentifiers: [storeId] });
    const product = products.find((p) => p.identifier === storeId);
    if (!product) {
      console.error('[IAP] Product not found in store:', storeId);
      return { productId, receipt: '', status: 'failed', error: `Product ${storeId} not found in store.` };
    }
    // Step 2: trigger the StoreKit / Play purchase sheet.
    const res = await rc.purchaseStoreProduct({ product });
    return {
      productId,
      receipt: res.transaction?.transactionIdentifier ?? '',
      status: 'completed',
    };
  } catch (e) {
    // Surface the real reason instead of swallowing it — a silent catch made the
    // Subscribe tap look broken ("按了没反应") and hid the StoreKit/RC cause
    // (offering not configured, Paid Apps agreement, etc.). User-cancel is not a
    // failure; everything else is logged (device console) + returned for the UI.
    const err = e as { code?: string | number; userCancelled?: boolean; message?: string };
    const cancelled = err?.userCancelled === true || /cancel/i.test(String(err?.code ?? ''));
    if (!cancelled) {
      console.error('[IAP] purchaseStoreProduct failed for', storeId, err);
    }
    return {
      productId,
      receipt: '',
      status: 'failed',
      cancelled,
      error: String(err?.message ?? err?.code ?? 'Purchase could not be started.'),
    };
  }
}

/**
 * Stable cross-device account key, derived from RevenueCat's `originalAppUserId`.
 *
 * On a fresh install this equals the device uid we configured RC with. After a
 * paying user reinstalls and restores, RC aliases the new id back to their
 * original customer (verified against RevenueCat docs), so originalAppUserId
 * resolves to the SAME value — exactly the key their cloud snapshot is stored
 * under. Returns null on web / before configure / RC unreachable.
 */
export async function getAppUserId(): Promise<string | null> {
  const rc = getRC();
  if (!rc) return null;
  try {
    const res = await rc.getCustomerInfo();
    const ci = (res.customerInfo ?? {}) as { originalAppUserId?: string };
    return typeof ci.originalAppUserId === 'string' && ci.originalAppUserId
      ? ci.originalAppUserId
      : null;
  } catch {
    return null;
  }
}

/** Restore prior purchases (App Store / Google Play). Entitlement is read
 *  separately via fetchProState(); callers sync vipState after restore. */
export async function restorePurchases(): Promise<PurchaseResult[]> {
  const rc = getRC();
  if (!rc) return [];
  try {
    await rc.restorePurchases();
    return [];
  } catch {
    return [];
  }
}

/**
 * Fudeto Pro entitlement, read from RevenueCat's server-verified customerInfo.
 *
 * Credential-free: RevenueCat validates the App Store / Play receipt on its own
 * backend; here we just read the result. Returns:
 *   - 'active' when the Pro subscription is currently entitled. RC keeps subs in
 *     `activeSubscriptions` through the billing grace period, so Pro correctly
 *     stays on during a recoverable lapse.
 *   - 'free'  on web (no IAP) or when no Pro subscription is active.
 *   - null    when RC is momentarily unreachable — callers MUST NOT downgrade a
 *     Pro user to 'free' on a null (keep the last known state instead).
 */
export async function fetchProState(): Promise<'active' | 'free' | null> {
  const rc = getRC();
  if (!rc) return 'free';
  try {
    const res = await rc.getCustomerInfo();
    const ci = (res.customerInfo ?? {}) as {
      activeSubscriptions?: string[];
      entitlements?: { active?: Record<string, unknown> };
    };
    if ((ci.activeSubscriptions ?? []).includes(SKU_TO_PRODUCT_ID.pro_monthly)) return 'active';
    if (Object.keys(ci.entitlements?.active ?? {}).length > 0) return 'active';
    return 'free';
  } catch {
    return null;
  }
}

/** Best-effort detection — used by screens that hide native-only CTAs.
 *  True on any native shell (iOS / Android via Capacitor); RC plugin is bundled there. */
export function hasNativeIAP(): boolean {
  return isNative();
}
